import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type * as JsSIPModule from "jssip";
import { api } from "@/api/client";
import type { CallStatus } from "./CallModal";
import { callModeOff, callModeOn } from "./callMode";
import { holdReload } from "@/lib/appUpdate";
import {
  createHoldMedia,
  enterHold,
  leaveHold,
  stepHold,
  warmHoldMusic,
  type HoldLine,
  type HoldSession,
  type HoldState,
} from "./callHold";

// WebRTC softphone over the office Asterisk (WSS). Config (ws server, SIP creds,
// gateway domain, test number, ICE servers) comes from chat-service so nothing
// is hardcoded in the bundle. The candidate is reached through the GSM trunk by
// the Asterisk dialplan.
//
// JsSIP (not SIP.js): the PBX runs SIP session timers — mid-call it expects a
// periodic session refresh, and a client that doesn't send one gets BYE'd after
// Session-Expires (the "call drops after a while" bug). JsSIP refreshes the
// session automatically (session_timers, on by default) and registers the
// endpoint, so the call stays up. This mirrors the working newwowanfrontend
// softphone, which runs against the same Asterisk.
export interface CallConfig {
  ws_server: string; // wss://host:8089/ws
  domain: string; // SIP domain for the request URI
  sip_user: string;
  sip_password: string;
  test_number: string; // canonical number dialled by «Тест»
  ice_servers?: RTCIceServer[]; // STUN + TURN (TURN relays media through NAT)
}

export interface CallStreams {
  local: MediaStream | null;
  remote: MediaStream | null;
}

type UA = InstanceType<typeof JsSIPModule.UA>;
type RTCSession = ReturnType<UA["call"]>;
type SinkAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

// JsSIP — пятая часть прежнего единого бандла (246 КБ из 1,1 МБ; 55 КБ gzip),
// а нужен он только с нажатия «Позвонить» или «Открыть звонок»: UA создаётся
// в start(), на старте приложения регистрации нет — входящие приходят
// событием chat-service, а прослушка входящего — тоже исходящий вызов.
// Поэтому отдельный кусок, который грузится параллельно с конфигом звонка,
// лишнего ожидания нет.
let jssip: Promise<typeof JsSIPModule> | null = null;
function loadJsSIP(): Promise<typeof JsSIPModule> {
  jssip ??= import("jssip").then(
    // CommonJS-пакет: сборка отдаёт его экспорты по именам, dev-сервер — ещё
    // и в default.
    (m) => ("UA" in m ? m : (m as unknown as { default: typeof JsSIPModule }).default),
    (err: unknown) => {
      // Не запоминать отказ: следующий звонок попробует снова.
      jssip = null;
      throw err;
    },
  );
  return jssip;
}

// ---- реестр активных звонков вкладки ----
//
// Софтфонов в приложении несколько (лента чата, карточка кандидата, окно
// входящего), и каждый — свой экземпляр хука со своим UA. А SIP-пользователь
// у всех один (101, max_contacts=1): второй звонок из той же вкладки сорвал бы
// регистрацию первому. Поэтому всплывашка входящего должна знать, что вкладка
// уже разговаривает, — счётчик здесь, на уровне модуля.
let activeSessions = 0;
const activeSubscribers = new Set<() => void>();

let releaseReload: (() => void) | null = null;
function bumpActive(delta: 1 | -1) {
  const was = activeSessions;
  activeSessions = Math.max(0, activeSessions + delta);
  // Телефон: «режим звонка» с первого звонка вкладки до конца последнего.
  // Включается раньше захвата микрофона — start() зовёт markActive первым.
  // И пока идёт звонок, страница ради новой версии не перезагрузится. А по
  // кнопке «Обновить» — только переспросив: beforeunload у звонка нет, и
  // браузер оборвал бы его без вопроса.
  if (!was && activeSessions) {
    callModeOn();
    releaseReload = holdReload("Идёт звонок — обновление страницы его прервёт. Обновить сейчас?");
  } else if (was && !activeSessions) {
    callModeOff();
    releaseReload?.();
    releaseReload = null;
  }
  for (const fn of [...activeSubscribers]) fn();
}

/** Есть ли в этой вкладке звонок: от «Соединение…» до разрыва линии. */
export function hasActiveCall(): boolean {
  return activeSessions > 0;
}

/** Подписка на изменения hasActiveCall() — под useSyncExternalStore. */
export function subscribeActiveCall(fn: () => void): () => void {
  activeSubscribers.add(fn);
  return () => {
    activeSubscribers.delete(fn);
  };
}

/** Нельзя начинать звонок из этого окна: само оно без звонка (idle, итог,
 * ошибка), а во вкладке уже идёт другой — например, перехваченный входящий,
 * свёрнутый ради карточки кандидата. Свой активный звонок окно показывает
 * кнопкой «Завершить», его это не касается. */
export function dialBlocked(own: CallStatus["state"], anyActive: boolean): boolean {
  const ownIdle = own === "idle" || own === "ended" || own === "error";
  return ownIdle && anyActive;
}

// ---- смена микрофона на живой линии ----

type MicTrack = Pick<MediaStreamTrack, "enabled" | "stop">;
export interface MicSender {
  readonly track: MicTrack | null;
  replaceTrack(track: MediaStreamTrack): Promise<void>;
}
export interface MicSwap {
  session: { isEnded(): boolean; isMuted(): { audio?: boolean } };
  /** Куда класть новую дорожку. Функция — цель выбирается ПОСЛЕ getUserMedia
   * (и после `beforeSwap`): пока шёл диалог разрешения, рекрутёр мог уйти на
   * удержание или вернуться, и тогда отправитель — не тот, что был в начале. */
  sender: MicSender | (() => MicSender);
  /** Ждём после getUserMedia, до чтения состояния линии: здесь setMic
   * дожидается, пока закончится идущий вход/выход удержания. */
  beforeSwap?: () => Promise<void>;
  getUserMedia: (c: MediaStreamConstraints) => Promise<MediaStream>;
  // Линия всё ещё та же (не положили трубку, не начали новую).
  sameSession: () => boolean;
  // Эта смена — последняя из наложившихся.
  latest: () => boolean;
}

/** Ядро setMic без React: новая дорожка в тот же RTP-отправитель
 * (replaceTrack), без переговоров по SDP. Возвращает новый поток; null —
 * остались на прежнем.
 *
 * Заглушённость ставится дважды. До replaceTrack — с прежней дорожки, иначе
 * смена микрофона во время прослушки на миг выпустила бы рекрутёра в эфир.
 * После — по флагу JsSIP: пока шёл replaceTrack, мог случиться перехват, и
 * unmute() включил тогда ещё старую дорожку (sender.track меняется, только
 * когда промис разрешится); новая так и осталась бы выключенной — звонящий
 * не слышит рекрутёра, а переключателя микрофона в окне нет. */
export async function swapMicTrack(deviceId: string, d: MicSwap): Promise<MediaStream | null> {
  let stream: MediaStream;
  try {
    stream = await d.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true });
  } catch {
    return null;
  }
  const drop = () => stream.getTracks().forEach((t) => t.stop());
  try {
    await d.beforeSwap?.();
  } catch {
    drop();
    return null;
  }
  // Пока ждали разрешения, могли выбрать другой микрофон или положить трубку.
  if (!d.latest() || !d.sameSession() || d.session.isEnded()) {
    drop();
    return null;
  }
  const track = stream.getAudioTracks()[0];
  if (!track) {
    drop();
    return null;
  }
  const sender = typeof d.sender === "function" ? d.sender() : d.sender;
  const prev = sender.track;
  track.enabled = prev ? prev.enabled : !d.session.isMuted().audio;
  try {
    await sender.replaceTrack(track);
  } catch {
    drop();
    return null;
  }
  if (!d.sameSession()) {
    drop();
    return null;
  }
  track.enabled = !d.session.isMuted().audio;
  return stream;
}

/** Очерёдность смены микрофона и удержания. Обе операции переставляют дорожки
 * в одном RTP-отправителе и держат «прежний микрофон» у себя в руках, поэтому
 * друг в друга не вклиниваются:
 *  - смена микрофона ждёт (после getUserMedia), пока идущий вход/выход
 *    удержания закончится, и только потом выбирает, куда класть дорожку;
 *  - пока смена микрофона переставляет дорожку, «Удержание» отклоняется.
 * Сам диалог разрешения getUserMedia ничего не блокирует. */
export function createHoldMicGate() {
  let holdIdle: Promise<void> | null = null;
  let swaps = 0;
  return {
    /** Вход/выход удержания идёт. */
    holdBusy: () => holdIdle !== null,
    /** Начать вход/выход удержания. null — идёт смена микрофона, отклонить
     * нажатие. Вернувшуюся функцию звать, когда вход/выход закончился. */
    beginHold(): (() => void) | null {
      if (holdIdle || swaps > 0) return null;
      let done!: () => void;
      holdIdle = new Promise<void>((r) => (done = r));
      return () => {
        holdIdle = null;
        done();
      };
    },
    /** Начать перестановку микрофона: дожидается конца удержания. Функцию
     * звать, когда перестановка кончилась (в том числе неудачно). */
    async beginSwap(): Promise<() => void> {
      while (holdIdle) await holdIdle;
      swaps += 1;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        swaps -= 1;
      };
    },
  };
}

// ---- микрофон на живой линии: «Без звука» ----

type Switchable = { enabled: boolean };
export interface LineMute {
  session: {
    isEnded(): boolean;
    mute(o: { audio: boolean }): void;
    unmute(o: { audio: boolean }): void;
  };
  // Аудиодорожки в RTP-отправителях соединения и дорожки локального потока:
  // после смены микрофона (swapMicTrack) это разные объекты.
  senderTracks: Switchable[];
  localTracks: Switchable[];
}

/** Выключить или включить микрофон на живой линии. Сначала флаг JsSIP (иначе
 * он вернёт заглушку при первом же re-INVITE, а swapMicTrack берёт состояние
 * новой дорожки именно из него), потом сами дорожки — на случай, если флаг так
 * и не был поднят. false — линии нет: ничего не тронуто. Тем же пользуется
 * перехват входящего (там микрофон только включается). */
export function setLineMuted(d: LineMute, muted: boolean): boolean {
  if (d.session.isEnded()) return false;
  try {
    if (muted) d.session.mute({ audio: true });
    else d.session.unmute({ audio: true });
  } catch {
    /* ignore */
  }
  for (const t of d.senderTracks) t.enabled = !muted;
  for (const t of d.localTracks) t.enabled = !muted;
  return true;
}

// Не ждать, пока браузер соберёт ВСЕ ICE-кандидаты. JsSIP отправляет INVITE
// только по окончании сбора, а сбор ждёт ответа каждого STUN/TURN из
// ice_servers. С сети, откуда наш TURN не виден, это ~40 с тишины перед
// вызовом: 2026-09-23 с машины на VPN google STUN собирался 2,9 с, а TURN
// 77.239.253.206 так и не ответил — сбор закрылся через 39,9 с, и рекрутёр не
// успел подключиться к входящему, пока звонящий был в меню.
//
// Relay-кандидат (TURN) — лучший путь, с ним звоним сразу. Без него даём
// ICE_GRACE_MS после первого кандидата: host и srflx к этому времени уже есть,
// а офисной сети (и VPN) хватает host-кандидата АТС.
export const ICE_GRACE_MS = 1500;

export interface IceGate {
  onCandidate(e: { candidate: { type?: string | null }; ready: () => void }): void;
  cancel(): void;
}

export function createIceGate(graceMs: number = ICE_GRACE_MS): IceGate {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let done = false;
  const fire = (ready: () => void) => {
    if (done) return;
    done = true;
    if (timer !== undefined) clearTimeout(timer);
    ready();
  };
  return {
    onCandidate({ candidate, ready }) {
      if (done) return;
      if (candidate.type === "relay") {
        fire(ready);
        return;
      }
      if (timer === undefined) timer = setTimeout(() => fire(ready), graceMs);
    },
    cancel() {
      done = true;
      if (timer !== undefined) clearTimeout(timer);
    },
  };
}

// Цель вызова для прослушки приходит с бекенда и уходит в SIP URI как есть,
// поэтому пропускаем только то, из чего она и состоит (hrdevaspy<цифры>).
const RAW_TARGET_RE = /^[A-Za-z0-9._-]+$/;

/** Какой номер пометить на chat-service как набранный этим рекрутёром
 * (api.markDial). SIP-пользователь у всех один, а запись разговора АТС
 * присылает с одним номером — без метки исходящий ложится без владельца, и
 * напоминание после него некому поставить. null — метить нечего: «Тест»
 * звонит на эхо, а не кандидату. */
export function dialMarkNumber(number: string, test: boolean): string | null {
  if (test) return null;
  const digits = number.replace(/\D/g, "");
  return digits ? "+" + digits : null;
}

export function useSoftphone() {
  const [status, setStatus] = useState<CallStatus>({ state: "idle" });
  // Браузер не дал играть звук собеседника (play() отклонён — iOS вне жеста):
  // окно звонка показывает «Включить звук», нажатие зовёт resumeAudio.
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [streams, setStreams] = useState<CallStreams>({ local: null, remote: null });
  // Рекрутёр выключил микрофон кнопкой «Без звука». Не то же самое, что
  // заглушка прослушки (там состояние «listening» и свой текст).
  const [micMuted, setMicMutedState] = useState(false);
  const micMutedRef = useRef(false);
  const setMicMuted = useCallback((on: boolean) => {
    micMutedRef.current = on;
    setMicMutedState(on);
  }, []);
  // Удержание (callHold.ts): когда началось, null — линия не на удержании.
  // Рекрутёрский «Без звука» от него не зависит и возвращается как был.
  const [holdSince, setHoldSinceState] = useState<number | null>(null);
  const holdSinceRef = useRef<number | null>(null);
  const holdRef = useRef<HoldSession | null>(null); // поток музыки и прежний микрофон
  // Очередь удержания и смены микрофона (createHoldMicGate).
  const [holdGate] = useState(createHoldMicGate);
  const stateRef = useRef<CallStatus["state"]>("idle");
  const holdState = useCallback(
    (): HoldState => ({
      held: holdSinceRef.current !== null,
      since: holdSinceRef.current,
      micMuted: micMutedRef.current,
    }),
    [],
  );
  const applyHold = useCallback(
    (next: HoldState) => {
      holdSinceRef.current = next.since;
      setHoldSinceState(next.since);
      setMicMuted(next.micMuted);
    },
    [setMicMuted],
  );
  const uaRef = useRef<UA | null>(null);
  const sessionRef = useRef<RTCSession | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const manualRef = useRef(false); // we hung up (vs the far side)
  // Поколение звонка. Набор идёт через несколько await (конфиг, микрофон), и
  // «Завершить» в это время раньше ничего не отменяло: UA создавался уже после
  // того, как окно закрыли, и звонок уходил в никуда. Любой teardown сдвигает
  // поколение — продолжение старого набора видит это и сворачивается.
  const genRef = useRef(0);
  const activeRef = useRef(false); // этот экземпляр учтён в activeSessions
  const speakerRef = useRef(""); // выход, который сейчас должен звучать
  const micSeqRef = useRef(0); // последняя из наложившихся смен микрофона
  const iceGateRef = useRef<IceGate | null>(null); // ранний INVITE, см. createIceGate

  const markActive = useCallback((on: boolean) => {
    if (activeRef.current === on) return;
    activeRef.current = on;
    bumpActive(on ? 1 : -1);
  }, []);

  const teardown = useCallback(async () => {
    genRef.current += 1;
    iceGateRef.current?.cancel();
    iceGateRef.current = null;
    markActive(false);
    // Поток музыки закрывается вместе с линией; следующий звонок — с
    // включённым микрофоном и не на удержании.
    holdRef.current?.media.stop();
    holdRef.current = null;
    applyHold(stepHold(holdState(), { type: "reset" }));
    setMicMuted(false);
    setStreams({ local: null, remote: null });
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session && !session.isEnded()) {
      try {
        session.terminate();
      } catch {
        /* ignore */
      }
    }
    const ua = uaRef.current;
    uaRef.current = null;
    if (ua) {
      try {
        ua.stop();
      } catch {
        /* ignore */
      }
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.remove();
      audioRef.current = null;
    }
    setAudioBlocked(false);
  }, [markActive, setMicMuted, applyHold, holdState]);

  // Хост звонка может исчезнуть посреди разговора: «Назад» закрывает карточку
  // кандидата, выход из системы размонтирует весь шелл. Без этого UA,
  // микрофон и <audio> оставались жить сами по себе — а с ними и линия на АТС.
  // Под StrictMode первый прогон очистки безвреден: рефы ещё пусты.
  useEffect(
    () => () => {
      manualRef.current = true;
      void teardown();
    },
    [teardown],
  );

  // Manual hangup — кнопка «Завершить».
  //
  // Состоявшийся разговор кончается «завершён», кто бы ни положил трубку
  // первым: держать окно до нажатия кнопки незачем, а рекрутёр как раз чаще
  // кладёт первым. Раньше своя трубка уводила в idle, и всё, что привязано к
  // концу разговора, после неё не срабатывало.
  //
  // Несостоявшийся вызов и эхо-тест — обратно в idle: оттуда логично набрать
  // снова, не закрывая окно. Прослушка входящего — тоже в idle: разговора не
  // было, окно закрывает тот, кто его открыл.
  const hangup = useCallback(async () => {
    manualRef.current = true;
    await teardown();
    setStatus((prev) => {
      if (prev.state === "in_call") {
        return { state: "ended", duration_s: Math.round((Date.now() - prev.since) / 1000) };
      }
      // Собеседник уже положил трубку — состояние с длительностью уже верное,
      // и затирать его нулём нельзя.
      return prev.state === "ended" ? prev : { state: "idle" };
    });
  }, [teardown]);

  // Смена микрофона посреди звонка (см. swapMicTrack). false — остались на
  // прежнем (в том числе когда линии ещё нет: выбор до подтверждения
  // применяет тот, кто её поднял).
  const setMic = useCallback(async (deviceId: string): Promise<boolean> => {
    const session = sessionRef.current;
    const pc = session?.connection as RTCPeerConnection | undefined;
    const sender = pc?.getSenders().find((s) => s.track?.kind === "audio");
    if (!session || session.isEnded() || !sender) return false;
    const seq = ++micSeqRef.current;
    // На удержании в отправителе — музыка, и новый микрофон в неё не встаёт:
    // он становится «прежним» и вернётся на выходе из удержания. Что сейчас —
    // удержание или нет — решается ПОСЛЕ getUserMedia (диалог разрешения мог
    // длиться сколько угодно): swapMicTrack зовёт beforeSwap, и тот дожидается
    // конца идущего входа/выхода, а пока дорожка переставляется, «Удержание»
    // не принимается (holdGate).
    const swap: { held: HoldSession | null; release: (() => void) | null } = {
      held: null,
      release: null,
    };
    try {
      const stream = await swapMicTrack(deviceId, {
        session,
        sender: () => {
          const h = holdRef.current;
          swap.held = h;
          if (!h) return sender;
          return {
            get track() {
              return h.mic;
            },
            replaceTrack: async (t: MediaStreamTrack) => {
              h.mic = t;
            },
          };
        },
        beforeSwap: async () => {
          swap.release = await holdGate.beginSwap();
        },
        getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c),
        sameSession: () => sessionRef.current === session,
        latest: () => seq === micSeqRef.current,
      });
      if (!stream) return false;
      // Новый микрофон ждёт выхода из удержания выключенным (волна — плоская).
      // Перестановка ещё не отпущена, удержание не могло смениться.
      if (swap.held) stream.getAudioTracks().forEach((t) => (t.enabled = false));
      const old = localStreamRef.current;
      localStreamRef.current = stream;
      old?.getTracks().forEach((t) => t.stop());
      // Волна рекрутёра перепривязывается к новому потоку (CallWaves зависит от него).
      setStreams((s) => ({ ...s, local: stream }));
      return true;
    } finally {
      swap.release?.();
    }
  }, [holdGate]);

  // Смена аудиовыхода посреди звонка. Отвалившееся устройство отклоняет
  // setSinkId — тогда системный выход, чтобы звук хоть где-то был.
  const setSpeaker = useCallback((deviceId: string) => {
    speakerRef.current = deviceId;
    const el = audioRef.current as SinkAudio | null;
    if (!el || typeof el.setSinkId !== "function") return;
    el.setSinkId(deviceId).catch(() => el.setSinkId?.("").catch(() => {}));
  }, []);

  // Перехват входящего: микрофон в эфир, отсчёт разговора — с нуля. Сначала
  // флаг JsSIP (иначе он вернёт заглушку при первом же re-INVITE), потом сами
  // дорожки — на случай, если флаг так и не был поднят.
  const lineMute = useCallback((muted: boolean): boolean => {
    const session = sessionRef.current;
    if (!session) return false;
    const pc = session.connection as RTCPeerConnection | undefined;
    return setLineMuted(
      {
        session,
        senderTracks: (pc?.getSenders() ?? []).flatMap((s) => (s.track?.kind === "audio" ? [s.track] : [])),
        localTracks: localStreamRef.current?.getAudioTracks() ?? [],
      },
      muted,
    );
  }, []);

  const unmute = useCallback((): boolean => {
    if (!lineMute(false)) return false;
    setMicMuted(false);
    setStatus((prev) => (prev.state === "listening" ? { state: "in_call", since: Date.now() } : prev));
    return true;
  }, [lineMute, setMicMuted]);

  // «Без звука» / «Включить микрофон» посреди разговора. Только живая линия:
  // до ответа и во время прослушки микрофон уже выключен как положено.
  const toggleMic = useCallback((): boolean => {
    const cur = holdState();
    const next = stepHold(cur, { type: "toggle_mic" });
    // На удержании микрофон выключен самим удержанием.
    if (next === cur) return false;
    if (!lineMute(next.micMuted)) return false;
    applyHold(next);
    return true;
  }, [lineMute, applyHold, holdState]);

  // «Удержание» / «Вернуться к разговору». Только живой разговор (до ответа и
  // в прослушке держать нечего). Нажатия, пришедшие, пока прошлое ещё
  // выполняется, отбрасываются: вход занимает доли секунды (музыка грузится).
  // true — состояние сменилось.
  const toggleHold = useCallback(async (): Promise<boolean> => {
    const session = sessionRef.current;
    const pc = session?.connection as RTCPeerConnection | undefined;
    const sender = pc?.getSenders().find((s) => s.track?.kind === "audio");
    if (!session || session.isEnded() || !pc || !sender) return false;
    const cur = holdState();
    if (!cur.held && stateRef.current !== "in_call") return false;
    const gen = genRef.current;
    const isCurrent = () => gen === genRef.current && sessionRef.current === session;
    const line: HoldLine = {
      isEnded: () => session.isEnded(),
      setFlag: (muted) => {
        try {
          if (muted) session.mute({ audio: true });
          else session.unmute({ audio: true });
        } catch {
          /* ignore */
        }
      },
      senderTracks: () => pc.getSenders().flatMap((s) => (s.track?.kind === "audio" ? [s.track] : [])),
      localTracks: () => localStreamRef.current?.getAudioTracks() ?? [],
      sender,
    };
    // Микрофон — обратно в то состояние «Без звука», что выбрал рекрутёр.
    const restoreMic = (muted: boolean) =>
      setLineMuted(
        { session, senderTracks: line.senderTracks(), localTracks: line.localTracks() },
        muted,
      );
    // Идёт прошлое нажатие или смена микрофона — это нажатие отбрасывается.
    const releaseHold = holdGate.beginHold();
    if (!releaseHold) return false;
    try {
      if (cur.held) {
        const hold = holdRef.current;
        if (!hold) {
          // Музыка так и не встала (вход оборвался) — вернуть только микрофон.
          applyHold(stepHold(cur, { type: "resume" }));
          restoreMic(cur.micMuted);
          return true;
        }
        if (!(await leaveHold(line, hold, cur.micMuted))) return false;
        if (holdRef.current === hold) holdRef.current = null;
        if (isCurrent()) applyHold(stepHold(holdState(), { type: "resume" }));
        return true;
      }
      // Создаётся синхронно, в нажатии: AudioContext без жеста не запустится.
      const media = createHoldMedia();
      if (!media) return false;
      applyHold(stepHold(cur, { type: "hold", now: Date.now() }));
      const hold = await enterHold(line, media, isCurrent);
      if (!hold) {
        if (isCurrent()) {
          // Не удалось: снова в разговор, «Без звука» — как было.
          applyHold(stepHold(holdState(), { type: "resume" }));
          restoreMic(cur.micMuted);
        }
        return false;
      }
      if (isCurrent()) holdRef.current = hold;
      else hold.media.stop();
      return true;
    } finally {
      releaseHold();
    }
  }, [applyHold, holdState, holdGate]);

  const start = useCallback(
    async ({
      target,
      micId,
      speakerId,
      listen,
      mark = null,
    }: {
      target: (cfg: CallConfig) => string;
      micId: string;
      speakerId: string;
      // Прослушка входящего: микрофон заглушён с первого пакета, после
      // подтверждения — «listening», а не «in_call».
      listen: boolean;
      // Номер для метки «набрал я» (dialMarkNumber); null — не метить.
      mark?: string | null;
    }) => {
      manualRef.current = false;
      setMicMuted(false);
      holdRef.current?.media.stop();
      holdRef.current = null;
      applyHold(stepHold(holdState(), { type: "reset" }));
      const gen = ++genRef.current;
      const stale = () => gen !== genRef.current;
      markActive(true);
      speakerRef.current = speakerId;
      setStatus({ state: "connecting" });

      // Dedicated <audio> for the remote leg, routed to the chosen output via
      // setSinkId. Создаётся ДО первого await и тут же запускается пустым
      // потоком: так play() случается в самом нажатии «Позвонить», и iOS
      // запоминает, что этому элементу играть разрешили. Звук собеседника
      // приходит много позже, уже вне жеста, — без этого iOS мог молча не
      // дать его играть: голос рекрутёра уходит, а сам он никого не слышит.
      const audio = document.createElement("audio");
      audio.autoplay = true;
      audio.setAttribute("playsinline", "");
      document.body.appendChild(audio);
      audioRef.current = audio;
      try {
        audio.srcObject = new MediaStream();
        void audio.play().catch(() => undefined);
      } catch {
        /* нет MediaStream — сыграет, когда придёт поток */
      }

      const sip = loadJsSIP();
      // Отказ разберём ниже, после конфига; до того — не «необработанный».
      sip.catch(() => undefined);

      let cfg: CallConfig;
      try {
        cfg = await api.callConfig();
      } catch {
        if (stale()) return;
        audio.remove();
        if (audioRef.current === audio) audioRef.current = null;
        markActive(false);
        setStatus({
          state: "error",
          message: "Телефония не настроена (нет WebRTC-эндпоинта на АТС).",
        });
        return;
      }
      if (stale()) return;

      let JsSIP: typeof JsSIPModule;
      try {
        JsSIP = await sip;
      } catch {
        if (stale()) return;
        audio.remove();
        if (audioRef.current === audio) audioRef.current = null;
        markActive(false);
        // Чаще всего кусок прошлой сборки стёрт выкладкой — новая версия уже на
        // сервере, плашку «Обновить» покажет lib/appUpdate (chunkFailed).
        setStatus({ state: "error", message: "Не загрузился модуль телефонии — обновите страницу." });
        return;
      }
      if (stale()) return;

      // One mic capture feeds both the call and the local level meter.
      let localStream: MediaStream;
      try {
        localStream = await navigator.mediaDevices.getUserMedia({
          audio: micId ? { deviceId: { exact: micId } } : true,
        });
      } catch {
        try {
          localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
          if (stale()) return;
          setStatus({ state: "error", message: "Нет доступа к микрофону." });
          await teardown();
          return;
        }
      }
      if (stale()) {
        localStream.getTracks().forEach((t) => t.stop());
        return;
      }
      // Прослушка: дорожку глушим ДО ua.call. mute() сразу после ua.call не
      // сработал бы — дорожки попадают в RTCPeerConnection микрозадачей позже,
      // отправителей ещё нет. Но и одного enabled=false мало: JsSIP сам
      // выставляет дорожкам enabled по своему флагу при hold/re-INVITE, поэтому
      // флаг поднимаем в `connecting` (см. ниже). Молчание при этом всё равно
      // уходит RTP-пакетами — у 101 rtp_timeout=30, без пакетов АТС положит
      // линию через полминуты. Поэтому никаких hold/recvonly.
      if (listen) localStream.getAudioTracks().forEach((t) => (t.enabled = false));
      localStreamRef.current = localStream;
      setStreams({ local: localStream, remote: null });

      // Attach the remote leg and make sure it plays on the chosen output. The
      // track fires on 183 early media (ringback / GSM tones) too, so the
      // recruiter hears call progress before the candidate picks up.
      const attachRemote = (pc: RTCPeerConnection) => {
        pc.addEventListener("track", (e) => {
          const el = audioRef.current;
          const remote = e.streams[0];
          if (!el || !remote) return;
          if (el.srcObject !== remote) el.srcObject = remote;
          el.muted = false;
          const sink = el as SinkAudio;
          const spk = speakerRef.current;
          if (spk && typeof sink.setSinkId === "function") {
            // A device that's gone (unplugged headset) rejects — fall back to
            // the system default so audio still comes out somewhere.
            sink.setSinkId(spk).catch(() => sink.setSinkId?.("").catch(() => {}));
          }
          el.play().then(
            () => setAudioBlocked(false),
            (err: unknown) => {
              // Не дали играть без жеста — просим нажать. Прочее (прервали
              // новым srcObject и т.п.) не наша забота.
              if (err instanceof DOMException && err.name === "NotAllowedError") setAudioBlocked(true);
            },
          );
          setStreams((s) => (s.remote === remote ? s : { ...s, remote }));
        });
      };

      const ua = new JsSIP.UA({
        sockets: [new JsSIP.WebSocketInterface(cfg.ws_server)],
        uri: `sip:${cfg.sip_user}@${cfg.domain}`,
        authorization_user: cfg.sip_user,
        password: cfg.sip_password,
        registrar_server: `sip:${cfg.domain}`,
        // Register (keeps the endpoint alive) and let JsSIP refresh the session
        // timer — the two things SIP.js didn't do that dropped the call.
        register: true,
        session_timers: true,
      });
      uaRef.current = ua;

      const dest = `sip:${target(cfg)}@${cfg.domain}`;

      let placed = false;
      const place = () => {
        if (placed || stale()) return;
        placed = true;
        // Прослушку АТС поднимает сразу (Answer), «Вызов…» там был бы враньём.
        if (!listen) setStatus({ state: "ringing" });
        const iceGate = createIceGate();
        iceGateRef.current = iceGate;
        const session = ua.call(dest, {
          mediaStream: localStream,
          pcConfig: {
            iceServers: cfg.ice_servers ?? [{ urls: "stun:stun.l.google.com:19302" }],
            rtcpMuxPolicy: "require",
          },
          eventHandlers: {
            peerconnection: (e) => attachRemote(e.peerconnection as RTCPeerConnection),
            icecandidate: (e) => iceGate.onCandidate(e),
            // `connecting` JsSIP шлёт уже после addTrack — теперь mute()
            // находит отправителей и запоминает флаг заглушки.
            connecting: () => {
              if (listen) sessionRef.current?.mute({ audio: true });
            },
            confirmed: () =>
              setStatus(
                listen ? { state: "listening", since: Date.now() } : { state: "in_call", since: Date.now() },
              ),
            ended: () => {
              if (manualRef.current) {
                manualRef.current = false;
                return;
              }
              // Длительность считаем здесь: в состоянии «завершён» её больше
              // неоткуда взять, а показать рекрутёру надо. Прослушка
              // разговором не считается — у неё длительность 0.
              setStatus((prev) => ({
                state: "ended",
                duration_s:
                  prev.state === "in_call" ? Math.round((Date.now() - prev.since) / 1000) : 0,
              }));
              teardown();
            },
            failed: (e) => {
              if (manualRef.current) {
                manualRef.current = false;
                return;
              }
              setStatus({
                state: "error",
                // Прослушку АТС отклоняет, когда токен уже не годен (звонок
                // взяли или он кончился, пока браузер дозванивался).
                message: listen
                  ? `Не удалось подключиться к звонку (${e.cause}).`
                  : `Звонок не удался (${e.cause}).`,
              });
              teardown();
            },
          },
        });
        sessionRef.current = session;
        // Belt & suspenders: attach even if the peerconnection event was missed.
        if (session.connection) attachRemote(session.connection as RTCPeerConnection);
        // Кто звонит — метка на chat-service, когда INVITE уже ушёл, а не по
        // нажатию: запись сверяется с ней по времени (окно в пару минут, как
        // у метки перехвата), и долгий запрос микрофона на первом звонке увёл
        // бы метку за это окно. Без
        // ожидания и без последствий для звонка: не дошла — запись просто
        // ляжет без рекрутёра, как раньше.
        if (mark) void api.markDial(mark).catch(() => undefined);
      };

      // ua.call must run after the WSS is up.
      ua.on("connected", () => place());
      ua.on("disconnected", () => {
        if (!placed && !stale()) {
          setStatus({ state: "error", message: "Нет связи с телефонией (WebRTC)." });
          teardown();
        }
      });
      // registrationFailed is not fatal — the INVITE authenticates on its own;
      // registration is only a keepalive. Swallow it (JsSIP logs it).
      ua.start();
    },
    [teardown, markActive, setMicMuted, applyHold, holdState],
  );

  const dial = useCallback(
    ({
      number,
      test,
      micId,
      speakerId,
    }: {
      number: string;
      test: boolean;
      micId: string;
      speakerId: string;
    }): Promise<void> =>
      start({
        target: (cfg) => (test ? cfg.test_number : "+" + number.replace(/\D/g, "")),
        micId,
        speakerId,
        listen: false,
        mark: dialMarkNumber(number, test),
      }),
    [start],
  );

  // Прослушка входящего: исходящий вызов на служебную цель, которую выдал
  // бекенд (`hrdevaspy<токен>`), — не на номер, поэтому без «+» и без чистки
  // до цифр. Входящий INVITE браузеру АТС послать не может: SIP-пользователь
  // у всех рекрутёров один, регистрация — у того, кто подключился последним.
  const listen = useCallback(
    ({ target, micId, speakerId }: { target: string; micId: string; speakerId: string }): Promise<void> => {
      if (!RAW_TARGET_RE.test(target)) {
        setStatus({ state: "error", message: "Некорректная линия прослушки." });
        return Promise.resolve();
      }
      return start({ target: () => target, micId, speakerId, listen: true });
    },
    [start],
  );

  const reset = useCallback(() => setStatus({ state: "idle" }), []);

  // Состояние линии для toggleHold: он читает его из колбэка, не из рендера.
  stateRef.current = status.state;
  // Музыку подкачиваем, как только линия поднята: к нажатию она уже в памяти.
  useEffect(() => {
    if (status.state === "in_call") warmHoldMusic();
  }, [status.state]);

  // «Включить звук»: play() в нажатии — теперь с жестом, iOS разрешит.
  const resumeAudio = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.play().then(
      () => setAudioBlocked(false),
      () => undefined,
    );
  }, []);

  return useMemo(
    () => ({
      status,
      streams,
      dial,
      listen,
      hangup,
      reset,
      unmute,
      micMuted,
      toggleMic,
      held: holdSince !== null,
      holdSince,
      toggleHold,
      setMic,
      setSpeaker,
      audioBlocked,
      resumeAudio,
    }),
    [
      status,
      streams,
      dial,
      listen,
      hangup,
      reset,
      unmute,
      micMuted,
      toggleMic,
      holdSince,
      toggleHold,
      setMic,
      setSpeaker,
      audioBlocked,
      resumeAudio,
    ],
  );
}

export type Softphone = ReturnType<typeof useSoftphone>;
